import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit
} from '@angular/core';
import { HistoricalChartComponent } from '../historical-chart/historical-chart.component';
import { CommonService } from 'app/core/services/common.service';
import { ChartsCommonService } from '../../../services/charts-common.service';
import { ChartsDashboardQuery } from '../../../state/dashboard';
import {
  ChartsDashboardSeriesQuery,
  IChartSeries
} from '../../../state/series';
import { ChartsDashboardCardQuery } from '../../../state/card';
import { ChartsHttpService } from '../../../services/charts-http.service';
import {
  Observable,
  zip
} from 'rxjs';
import {
  Datetime,
  IChartFilter,
  IIntraDayOptionChartParameters,
  ILCPoint,
  IMappedSeriesSingleContractAnalyzerPoints,
  ISingleContractAnalyzerChartPoint,
  RealTimeEnum
} from '../../../interfaces';
import { map } from 'rxjs/operators';
import { ID } from '@datorama/akita';
import {
  LineSeries,
  SeriesXY
} from '@arction/lcjs';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';

@Component({
  selector: 'iv-live-single-contract-analyzer',
  templateUrl: '../common-chart/common-chart.component.html',
  styleUrls: ['../common-chart/common-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SingleContractAnalyzerComponent extends HistoricalChartComponent implements OnInit, OnDestroy {

  constructor(
    protected readonly commonService: CommonService,
    protected readonly chartsCommonService: ChartsCommonService,
    protected readonly elementRef: ElementRef,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dashboardQuery: ChartsDashboardQuery,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery,
    protected readonly cardsQuery: ChartsDashboardCardQuery,
    protected readonly httpService: ChartsHttpService,
    protected readonly chartsState: ChartsDashboardStateService
  ) {
    super(commonService, chartsCommonService, elementRef, changeDetectorRef, dashboardQuery, seriesQuery, cardsQuery, httpService, chartsState);
  }

  protected loadSeriesPoints(seriesIds: ID[], isFirstLoad?: boolean): void {
    const series: IChartSeries[] = this.seriesQuery.getAll().filter((_series: IChartSeries) => seriesIds.find(id => id === _series.id));

    zip(...this.createSeriesObservables(series, isFirstLoad))
      .subscribe((res: IMappedSeriesSingleContractAnalyzerPoints[]) => {
        res.forEach((pointsData: IMappedSeriesSingleContractAnalyzerPoints) => {
          if (
            isFirstLoad &&
            this.chart.getDefaultAxisX().getTickStrategy() === 'Numeric' &&
            pointsData.seriesId === this.mainSeries.id
          ) {
            this.setTickStrategy(pointsData.points);
          }
          this.fillLineSeries(pointsData, this.mainSeries?.id === pointsData.seriesId, isFirstLoad);
          this.pointsData = {
            ...this.pointsData,
            [pointsData.seriesId]: pointsData.points
          };
        });
      });
  }


  protected loadAllSeriesPoints(isFirstLoad?: boolean): void {
    zip(...this.createSeriesObservables(this.allSeries, true))
      .subscribe((res: IMappedSeriesSingleContractAnalyzerPoints[]) => {
        if (isFirstLoad) {
          this.setTickStrategy(
            res.find((pointsData: IMappedSeriesSingleContractAnalyzerPoints) => pointsData.seriesId === this.mainSeries.id).points
          );
          this.setCursorLegendAxis(res[0]);
        }

        res.forEach((pointsData: IMappedSeriesSingleContractAnalyzerPoints, index: number) => {
          this.fillLineSeries(pointsData, !index, isFirstLoad);
          this.pointsData = {
            ...this.pointsData,
            [pointsData.seriesId]: pointsData.points
          };
        });
      });
  }

  protected createSeriesObservables(seriesArr: IChartSeries[], isFirstLoad?: boolean): Observable<IMappedSeriesSingleContractAnalyzerPoints>[] {
    return seriesArr.map((series: IChartSeries) => {
      return this.httpService.getContractView(this.getChartsParams(series, isFirstLoad) as IIntraDayOptionChartParameters)
        .pipe(
          map((data: ISingleContractAnalyzerChartPoint[]) => {
            return {
              points: data,
              seriesId: series.id
            };
          })
        );
    });
  }

  protected setOriginDate(firstMainSeriesPoint: ISingleContractAnalyzerChartPoint): void {
    this.originDate = new Date(firstMainSeriesPoint.time);
  }

  protected mapPoints(pointsData: IMappedSeriesSingleContractAnalyzerPoints): ILCPoint[] {
    if (!this.originDate && pointsData.points.length) {
      this.setOriginDate(pointsData[0]);
    }

    const series: IChartSeries = this.seriesQuery.getEntity(pointsData.seriesId);

    return pointsData.points.map((item: ISingleContractAnalyzerChartPoint) => {
      return {x: new Date(item.time).getTime() - this.originDate.getTime(), y: +item[series.selectedFilters.singleContractAnalyzerType.name]};
    });
  }

  protected setCursorResultTableFormatter(lineSeries: LineSeries, series: IChartSeries, filters?: IChartFilter[], doNotShowAxisZ?: boolean): void {
    if (!lineSeries) return;

    lineSeries
      .setCursorResultTableFormatter((builder, _series: SeriesXY, x: number, y: number, dataPoint: ILCPoint) => {
        this.builderCursorResult = builder;
        this.builderCursorResult
          .addRow(`Date ${_series.axisX.formatValue(x)}`.replace(',', ''))
          .addRow(`${series.stock.symbol}`)
          .addRow(`${series.selectedFilters.singleContractAnalyzerType.description}: ${Math.round((y + Number.EPSILON) * 100) / 100}`);

        return this.builderCursorResult;
      });
  }

  protected setCursorLegendAxis(data: IMappedSeriesSingleContractAnalyzerPoints): void {
    this.cursorResult = {
      x: data.points[0].time,
      y: data.points[0][this.seriesQuery.getEntity(data.seriesId).selectedFilters.singleContractAnalyzerType.name].toString(),
      z: undefined
    };
  }

  protected addPointsFromRt(data: IMappedSeriesSingleContractAnalyzerPoints): void {
    if (!data.points.length) return;
    // Проверка на одинаковые точки. Если у нас уже есть эта точка - мы ее не добавляем
    if (
      data.points.length === 1 &&
      data.points[0].time === this.pointsData[data.seriesId][(this.pointsData[data.seriesId] as any).length - 1].time
    ) {
      return;
    }

    this.lineSeries[data.seriesId].add(this.mapPoints(data));
    this.pointsData[data.seriesId] = [
      ...this.pointsData[data.seriesId] as any,
      ...data.points
    ];
  }

  protected getFrom(series: IChartSeries): Datetime | undefined {
    if (!series) return;

    return this.pointsData[series.id] && series.selectedFilters.realtime?.name === RealTimeEnum.ON
      ? (this.pointsData[series.id] as ISingleContractAnalyzerChartPoint[]).length > 1
        ? this.pointsData[series.id][(this.pointsData[series.id] as ISingleContractAnalyzerChartPoint[]).length - 1].time
        : this.pointsData[series.id][0].time
      : undefined;
  }

  protected getChartsParams(series: IChartSeries, isFirstLoad?: boolean): IIntraDayOptionChartParameters {
    if (!series) return;

    let from: Datetime;

    if (!isFirstLoad) {
      from = this.getFrom(series);
    }

    return {
      stockId: series.stock.stockId.toString(),
      symbol: series.stock.symbol,
      from,
      timeRange: series.selectedFilters.timeRange.name,
      expDate: series.selectedFilters.expirationDate.name,
      strike: series.selectedFilters.strike.name.toString(),
      optType: series.selectedFilters.optionType.name,
      params: series.selectedFilters.singleContractAnalyzerType.name,
      frequency: '1', // TODO: Первая версия графика
      sendUserMetrics: !this.isSentUserMetrics && this.mainSeries.id === series.id
    };
  }
}
